import { useEffect, useState } from 'react';
import { useConfig } from '../context/ConfigContext';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import {
  getMinimumPartialAmount,
  validatePartialAmount,
} from '../utils/partialPaymentUtil';
import '../css/PartialPaymentModal.css';

/**
 * Captures the downpayment for "Confirm (Partial)".
 *
 * Shared by the Manage Booking modal and the Reservation Queue detail modal
 * (AdminBooking.jsx) — both confirm the same way and must enforce the same
 * minimum, so the field, the note and the validation live here once.
 *
 * Deliberately NOT closed by a backdrop click (CLAUDE.md > Behavior): it is
 * asking the admin to commit a money figure, and a stray tap outside used to
 * be the easiest way to lose one. The × and Cancel are the ways out.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string} [props.guestName] - shown in the sub-heading for context.
 * @param {number} props.total - the reservation's grand total.
 * @param {number} props.percent - the configured minimum percentage.
 * @param {boolean} [props.resolvingTotal] - the group's total is still being
 *   read from Firebase (a multi-date reservation needs its primary record).
 * @param {boolean} [props.saving] - the confirmation is being written.
 * @param {(amount: number) => void} props.onSubmit - called with the validated amount.
 * @param {() => void} props.onClose
 * @returns {JSX.Element|null}
 */
function PartialPaymentModal({
  isOpen,
  guestName = '',
  total = 0,
  percent = 0,
  resolvingTotal = false,
  saving = false,
  onSubmit,
  onClose,
}) {
  const { config } = useConfig();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  // A fresh field every time the modal opens — the previous reservation's
  // figure sitting in it is one careless Enter away from being accepted
  // against a booking it has nothing to do with.
  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const minimum = getMinimumPartialAmount(total, percent);

  /**
   * Validates the typed amount and hands it up, or shows why it can't be.
   * @returns {void}
   */
  const handleSubmit = () => {
    const result = validatePartialAmount(amount, total, percent, config);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError('');
    onSubmit(result.amount);
  };

  // Live, so the admin sees what is still owed as they type rather than
  // after the fact. Falls back to the full total on an unparseable field.
  const typed = Number.parseFloat(amount);
  const balancePreview = Number.isFinite(typed) && typed > 0
    ? Math.max(0, Math.round((total - typed) * 100) / 100)
    : total;

  return (
    <div className="ppm-overlay">
      <div className="ppm-card">
        <div className="ppm-head">
          <h2>Partial Payment</h2>
          <button type="button" className="ppm-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {resolvingTotal ? (
          <div className="ppm-loading">{spinnerRing('Loading reservation total')}</div>
        ) : (
          <>
            {guestName && <p className="ppm-guest">{guestName}</p>}

            <div className="ppm-row">
              <span>Total</span>
              <span className="ppm-total">{formatMoney(total, config)}</span>
            </div>

            {/* The club's rule, spelled out in pesos rather than left as a
                percentage for the admin to work out at the counter. */}
            <p className="ppm-note">
              For partial payment, amount should be at least <strong>{formatMoney(minimum, config)}</strong>
              {' '}({percent}% of {formatMoney(total, config)}).
            </p>

            <div className="ppm-field">
              <label htmlFor="ppm-amount">Amount received</label>
              <input
                id="ppm-amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                disabled={saving}
                placeholder={String(minimum)}
                onChange={(e) => { setAmount(e.target.value); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
              />
            </div>

            {error
              ? <p className="ppm-error">{error}</p>
              : <p className="ppm-balance">Balance after this payment: <strong>{formatMoney(balancePreview, config)}</strong></p>}

            <div className="ppm-actions">
              <button type="button" className="ppm-cancel" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className="ppm-submit" onClick={handleSubmit} disabled={saving}>
                {saving ? '…' : 'Confirm (Partial)'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PartialPaymentModal;
